import * as PIXI from "pixi.js"
import PixiScene from "../PixiScene";
import PixiUtil from "./PixiUtil";
import PixiCard from "./PixiCard";
import PixiMessenger from "./PixiMessenger";
import EventApi from "../../../../../api/EventsApi";
import {Player} from "../../../../../../types";
import PixiSelector from "./PixiSelector";

interface IButton {
    iconId: string;
    hint: string;
    subMenu?: boolean;
    off?: boolean;
}

const IC = {
    DEL: 'ic_delete',
    FLP: 'ic_flip',
    LCK: 'ic_lock',
    MAG: 'ic_magnifying_glass',
    MOV: 'ic_move_to_player',
    SHF: 'ic_shuffle',
    ALL: 'ic_to_all_players',
    HND: 'ic_to_hand',
    TBL: 'ic_to_table',
} as const;
const IC_UNL = 'ic_unlock';

const CAN = {
    DEL: 'canTableDeleteCards',
    FLP: 'canTableFlipCards',
    LCK: 'canTableLockCards',
    MAG: 'canTablePreviewCards',
    MOV: 'canTablePassCards',
    SHF: 'canShuffle',
    ALL: 'canTablePassCards',
    HND: 'canTableTakeCards',
    TBL: 'canTableTakeCards',
} as const;

export default class PixiPieMenu extends PIXI.Container {

    private readonly DEBUG = false;
    private readonly scene: PixiScene;
    private readonly pie = new PIXI.Graphics;
    private readonly iconContainer = new PIXI.Container;
    private readonly textStyle: PIXI.TextStyle;
    private readonly hint: PIXI.Text;

    private card: PixiCard | undefined;
    private config: Array<IButton> = [];
    private curTier = -1;
    private curSlices = [-1, -1];
    private icons = new Map<string, PIXI.Sprite>();
    private isOut = false;
    private isOutTierOn = false;
    private pileTooShort = false;
    private startAngle = 0;
    private subMenuLength = 0;

    private menuMap = new Map<string, boolean>();

    private readonly bgColor = {
        normal: 0x41454a,
        hover:  0xfe5616,
    };
    private readonly tiers = [
        { rIn:  58, rOut: 110, rInLim:   0, rOutLim: 118, nSlices:  6 },
        { rIn: 118, rOut: 150, rInLim: 118, rOutLim: 180, nSlices: 20 },
    ];

    constructor(scene: PixiScene) {
        super();
        this.name = 'pixiPieMenu';
        this.scene = scene;

        Object.values(IC).forEach(id => {
            this.setSvgIcon(id);
        });
        this.setSvgIcon(IC_UNL);

        this.textStyle = new PIXI.TextStyle({
            align: 'center',
            fontFamily: 'Arial',
            fontSize: 16,
            fontWeight: '100',
            fill: 0xffffff,
        });

        this.addChild(this.pie);
        this.addChild(this.iconContainer);

        this.hint = new PIXI.Text('Lorem\nipsum', this.textStyle);
        this.addChild(this.hint);
        this.hint.anchor.set(0.5);

        this.update();

        this.interactive = true;
        this.buttonMode  = true;
        this.on('pointermove', this.onHover);
        this.on('pointerup', this.onClick);

        Object.keys(IC).forEach(key => this.menuMap.set(
            IC[key as keyof typeof IC],
            this.scene.isBanned(CAN[key as keyof typeof CAN])
        ));
    }

    /**
     * Создание иконки
     */
    private setSvgIcon(id: string): void {
        const iconSvg = `/images/context-menu/${id}.svg`;
        const iconTexture = PIXI.Texture.from(iconSvg);
        const icon = new PIXI.Sprite(iconTexture);
        icon.anchor.set(0.5);
        this.icons.set(id, icon);
    }

    /**
     * Кнопка меню
     */
    private drawPieSlice(iTier: number, iSlice: number, rIn: number, rOut: number): void {
        const TAU = 2 * Math.PI;
        const startAngle = iTier ? this.startAngle : 0;
        const isHover = (iSlice === this.curSlices[iTier]);
        const bgColor = isHover ? this.bgColor.hover : this.bgColor.normal;
        const nSlices = iTier ? this.tiers[iTier].nSlices : this.config.length;
        const aStart = startAngle + iSlice / nSlices, aEnd = startAngle + (iSlice + 1) / nSlices;
        const hslColor = PixiUtil.hsl2hex(aStart * 360, 1, 0.33);

        const gapIn = Math.atan2(2, rIn) / TAU, gapOut = Math.atan2(2.5, rOut) / TAU;
        const aStartIn = aStart + gapIn, aEndIn = aEnd - gapIn;
        const aStartOut = aStart + gapOut, aEndOut = aEnd - gapOut;

        function getXY(r: number, a: number): PIXI.Point {
            return new PIXI.Point(r * Math.cos(a), r * Math.sin(a));
        }

        const cp01 = getXY(rOut, TAU * aStartOut);
        const cp23 = getXY(rOut, TAU * aEndOut);
        const cp45 = getXY(rIn, TAU * aEndIn);
        const cp67 = getXY(rIn, TAU * aStartIn);

        const rad = 8; // Radius of the corners
        const daIn = Math.atan2(rad, rIn);
        const daOut = Math.atan2(rad, rOut);

        const p0 = getXY(rOut - rad, TAU * aStartOut);
        const p1 = getXY(rOut, TAU * aStartOut + daOut);
        // const p2 = getXY(rOut, TAU * aEndOut - daOut);
        const p3 = getXY(rOut - rad, TAU * aEndOut);
        const p4 = getXY(rIn + rad, TAU * aEndIn);
        const p5 = getXY(rIn, TAU * aEndIn - daIn);
        // const p6 = getXY(rIn, TAU * aStartIn + daIn);
        const p7 = getXY(rIn + rad, TAU * aStartIn);

        let playerColor = bgColor;
        let solo = !this.extraPlayers.length;
        let fewCards = false;
        solo = this.DEBUG ? false : solo;
        const id = this.config[this.curSlices[0]]?.iconId ?? '';
        if (iTier && !solo && IC.MOV === id) {
            const player = this.extraPlayers[iSlice];
            if (player) {
                playerColor = PIXI.utils.string2hex(player.color.code);
            }
            playerColor = this.DEBUG ? hslColor : playerColor;
            if (isHover) {
                const hsv = PixiUtil.hex2hsv(playerColor);
                playerColor = PixiUtil.hsv2hex(hsv[0], hsv[1], hsv[2] / 2);
            }
        }
        this.pie.beginFill(playerColor || bgColor);

        this.pie.moveTo(p0.x, p0.y);
        this.pie.quadraticCurveTo(cp01.x, cp01.y, p1.x, p1.y);
        this.pie.arc(0, 0, rOut, TAU * aStartOut + daOut, TAU * aEndOut - daOut); //1-2
        this.pie.quadraticCurveTo(cp23.x, cp23.y, p3.x, p3.y);
        this.pie.lineTo(p4.x, p4.y);
        this.pie.quadraticCurveTo(cp45.x, cp45.y, p5.x, p5.y);
        this.pie.arc(0, 0, rIn, TAU * aEndIn - daIn, TAU * aStartIn + daIn, true); //5-6
        this.pie.quadraticCurveTo(cp67.x, cp67.y, p7.x, p7.y);
        this.pie.lineTo(p0.x, p0.y);
        this.pie.endFill();

        const pC = getXY(rIn + (rOut - rIn)/2, TAU * (aStart + (aEnd - aStart)/2));
        if (iTier) {
            if (playerColor === bgColor) {
                const text = new PIXI.Text('' + (iSlice + 1), this.textStyle);
                this.iconContainer.addChild(text);
                text.anchor.set(0.5);
                text.x = pC.x;
                text.y = pC.y;
            }
            else {
                const text = new PIXI.Text(isHover ? '•' : '', this.textStyle);
                this.iconContainer.addChild(text);
                text.anchor.set(0.5);
                text.x = pC.x;
                text.y = pC.y;
            }
        }
        else {
            const iconId = this.config[iSlice].iconId;
            const locked = (IC.LCK === iconId && this.card?.locked);
            const icon = this.icons.get(locked ? IC_UNL : iconId);
            if (icon) {
                icon.x = pC.x;
                icon.y = pC.y;
                this.iconContainer.addChild(icon);
            }
        }
    }

    /**
     * Кольцо меню
     */
    private drawPieTier(iTier: number): void {
        const tier = this.tiers[iTier];
        const n = iTier ? tier.nSlices : this.config.length;
        const list = iTier ? Array(n).fill(0) : this.config;

        let numPlayers = this.extraPlayers.length;
        numPlayers = this.DEBUG ? 6 : numPlayers;
        const pileLength = this.card?.pileLength ?? 0;

        this.subMenuLength = 0;
        const id = this.config[this.curSlices[0]]?.iconId ?? '';
        if (IC.MOV === id) {
            this.subMenuLength = numPlayers;
        }
        else if (IC.ALL === id) {
            this.subMenuLength = Math.min(20, Math.floor(pileLength / (numPlayers + 1)));
        }
        else if (IC.HND === id) {
            this.subMenuLength = Math.min(20, pileLength);
        }

        this.pileTooShort = pileLength < (numPlayers + 1);

        list.forEach((_, iSlice) => {
            if (!iTier || iSlice < this.subMenuLength) {
                this.drawPieSlice(iTier, iSlice, tier.rIn, tier.rOut);
            }
        });
    }

    /**
     * Обновить состояние
     */
    public update(): void {
        const rad = 50;
        this.hint.scale.x = 1;
        this.hint.text = this.config[this.curSlices[0]]?.hint ?? '';
        if (IC.LCK === this.config[this.curSlices[0]]?.iconId) {
            this.hint.text = this.card?.locked ? 'Unlock (L)' : 'Lock (L)';
        }

        const limWidth = 2 * (rad - 8);
        if (this.hint.width > limWidth) {
            this.hint.scale.x = limWidth / this.hint.width;
        }

        this.pie.clear();

        this.pie.beginFill(0xffffff, 0.001);
        this.pie.drawCircle(0, 0, 180);
        this.pie.endFill();

        this.pie.beginFill(this.bgColor.normal);
        this.pie.drawCircle(0, 0, rad);
        this.pie.endFill();

        this.iconContainer.removeChildren();
        this.drawPieTier(0);

        this.isOutTierOn = false;
        if (this.config[this.curSlices[0]]?.subMenu) {
            this.drawPieTier(1);
            this.isOutTierOn = true;
        }
    }

    /**
     * Переместить в точку
     */
    public moveTo(x: number, y: number): void
    {
        const padding = 16, R = this.tiers[1].rOut + padding;
        const clamp = (min:number, val:number, max:number) => Math.min(Math.max(min, val), max);
        const rect = this.scene.rect;
        if (rect) {
            this.x = clamp(R, x, rect.width  - R);
            this.y = clamp(R, y, rect.height - R);
        }
    }

    /**
     * Hover event handler
     */
    private onHover(event: PIXI.InteractionEvent): void {
        if (event.target !== event.currentTarget) {
            return;
        }
        event.data.originalEvent.stopPropagation();
        this.polarMath(this.toLocal(event.data.global));
    }

    /**
     * Click event handler
     */
    private onClick(event: PIXI.InteractionEvent): void {
        if (event.target !== event.currentTarget) {
            return;
        }
        event.data.originalEvent.stopPropagation();

        if (!this.card || this.curSlices[0] < 0) {
            return;
        }
        if (!this.isOut) {
            this.commitAction(this.curSlices[0], this.curSlices[1]);
        }
        this.closeMenu();
    }

    /**
     * polar Math
     */
    private polarMath(p: PIXI.Point): void {
        const TAU = 2 * Math.PI;
        let curTier = -1, curSlice = -1;
        let distance = Math.hypot(p.x, p.y), angle = Math.atan2(p.y, p.x);
        angle = (angle < 0 ? angle + TAU : angle) / TAU;

        this.isOut = distance > this.tiers[this.isOutTierOn? 1: 0].rOutLim;

        this.tiers.forEach((tier, iTier) => {
            if (curTier < 0 && tier.rInLim <= distance && distance < tier.rOutLim) {
                curTier = iTier;
                const startAngle = curTier ? this.startAngle : 0;
                let rotatedAngle = angle - startAngle;
                rotatedAngle = (rotatedAngle < 0) ? rotatedAngle + 1 : rotatedAngle;
                const nSlices = curTier ? this.tiers[curTier].nSlices : this.config.length;
                Array(nSlices).fill(0).forEach((_, iSlice) => {
                    if (curSlice < 0 && iSlice/nSlices < rotatedAngle && rotatedAngle < (iSlice + 1)/nSlices) {
                        curSlice = iSlice;
                        if (!curTier) {
                            this.startAngle = iSlice / nSlices;
                        }
                    }
                });
            }
        });

        let mustUpdate = false;
        if (this.curTier !== curTier) {
            this.curTier = curTier;
            if (!curTier) {
                this.curSlices[1] = 0;
            }
            mustUpdate = true;
        }
        else {
            if (this.curSlices[curTier] !== curSlice) {
                this.curSlices[curTier] = curSlice;
                mustUpdate = true;
            }
        }
        if (mustUpdate) {
            this.update();
        }

    }

    private get extraPlayers(): Array<Player>
    {
        return this.scene.curPlayers
            .filter(player => !player.isGuest && player.id !== this.scene.playerId);
    }

    /**
     * Открыть меню
     */
    public openMenu(card: PixiCard): void
    {
        if (card) {
            this.card = card;
            if ('hand' === card.zone) {
                this.config = [
                    { iconId: IC.MOV, hint: 'Pass to\nplayer', subMenu: true },
                    { iconId: IC.TBL, hint: 'Put on\ntable (T)' },
                    { iconId: IC.FLP, hint: 'Flip (F)' },
                    { iconId: IC.MAG, hint: 'Preview\n(Space)' },
                    { iconId: IC.DEL, hint: 'Delete' },
                ];
            }
            else if ('table' === card.zone) {
                if (1 === card.pileLength) {
                    this.config = [
                        { iconId: IC.DEL, hint: 'Delete' },
                        { iconId: IC.MOV, hint: 'Pass to\nplayer', subMenu: true },
                        { iconId: IC.HND, hint: 'Take in\nhand (H)' },
                        { iconId: IC.FLP, hint: 'Flip (F)' },
                        { iconId: IC.MAG, hint: 'Preview\n(Space)' },
                        { iconId: IC.LCK, hint: 'Lock (L)' },
                    ];
                }
                else {
                    this.config = [
                        { iconId: IC.DEL, hint: 'Delete' },
                        { iconId: IC.ALL, hint: 'Deal X cards\nto all', subMenu: true },
                        { iconId: IC.HND, hint: 'Take X cards\nin hand', subMenu: true },
                        { iconId: IC.SHF, hint: 'Shuffle\n(S)' },
                        { iconId: IC.FLP, hint: 'Flip (F)' },
                        { iconId: IC.LCK, hint: 'Lock (L)' },
                    ];
                }
            }

            this.config = this.config.filter(it => {
                let canNotDo = !!this.menuMap.get(it.iconId);
                if (IC.MOV === it.iconId) {
                    canNotDo = canNotDo || (this.DEBUG ? false : !this.extraPlayers.length);
                }
                if (IC.ALL === it.iconId) {
                    const numPlayers = this.DEBUG ? 6 : this.extraPlayers.length;
                    const pileLength = this.card?.pileLength ?? 0;
                    canNotDo = canNotDo || (pileLength < (numPlayers + 1));
                }
                return !canNotDo;
            });

            if (this.config.length) {
                this.update();
                this.visible = true;
            }
        }
    }

    /**
     * Закрыть меню
     */
    public closeMenu(): void
    {
        if (this.visible) {
            this.visible = false;
            this.curTier = -1;
            this.curSlices = [-1, -1];
            this.card = undefined;
            this.scene.onCloseContextMenu();
        }
    }

    /**
     * Выполнить команду
     */
    private commitAction(actionId: number, data: number): void
    {
        if (!this.card || actionId < 0) {
            return;
        }
        const id = this.config[actionId].iconId;
        switch (id) {
            case IC.DEL:
                PixiMessenger.deleteCards(this.card, this.scene.isAdminStatus);
                break;
            case IC.FLP:
                PixiMessenger.sendFlip(this.card);
                break;
            case IC.LCK:
                PixiMessenger.lockTableItems(this.card);
                break;
            case IC.SHF:
                PixiMessenger.shuffleSelectedTableItems(this.card);
                break;
            case IC.HND:
                if (1 === this.card.pileLength) {
                    PixiMessenger.moveToHandOrTable(this.card);
                }
                else {
                    const num = this.config[actionId].subMenu ? (data + 1) : 0;
                    PixiMessenger.moveNCardsToPlayer(this.card, num);
                }
                break;
            case IC.TBL:
                PixiMessenger.moveToHandOrTable(this.card);
                break;
            case IC.MAG:
                if (!this.scene.isBanned('canTablePreviewCards')) {

                    EventApi.triggerEvent(EventApi.EDIT_START, {
                        cardId: this.card.cardId,
                        pileId: this.card.pileId,
                        actions: (this.card.config?.paint?.actions ?? []),
                        bgSrc: this.card.bgSrc,
                        playerColorCode: this.scene.playerColorCode
                    });

                    EventApi.triggerEvent(EventApi.ZOOM_ITEMS, [this.card.cardSrc]);
                }
                break;
            case IC.MOV:
                const player = this.extraPlayers[data];
                if (player && player.id) {
                    PixiMessenger.moveToPlayer(this.card, player.id);
                }
                break;
            case IC.ALL:
                if (!this.pileTooShort) {
                    const num = this.config[actionId].subMenu ? (data + 1) : 0;
                    PixiMessenger.dealNCards(this.card, num);
                }
                break;
            default:
                console.log('%c ### Action:', 'color: #f0f', id, data, this.card);
        }
        PixiSelector.clearAll();
    }

    /**
     * Id владеющей карты
     */
    public get ownerId(): number
    {
        return this.card?.pileId ?? 0;
    }

}
